import { inject, Injectable } from '@angular/core';
import { selectEntity, upsertEntities } from '@ngneat/elf-entities';
import { ManagementService } from '@thiememeulenhoff/ng-api-gateway';
import { BehaviorSubject, map, tap } from 'rxjs';
import {
  ManagementMergeConflictsResponse,
  ManagementResult,
  ManagementStatistic,
  Result,
} from '@thiememeulenhoff/types';
import * as stores from './management.store';

@Injectable({
  providedIn: 'root',
})
export class ManagementFacade {
  readonly #managementResults = stores.managementResults;

  readonly #managementStatistics = stores.managementStatistics;

  readonly #managementService = inject(ManagementService);

  readonly #managementMergeConflictsSubject =
    new BehaviorSubject<ManagementMergeConflictsResponse | null>(null);

  readonly managementMergeConflicts$ =
    this.#managementMergeConflictsSubject.asObservable();

  readonly managementResult$ = (actorId: string, arrangementCode: string) =>
    this.#managementResults.pipe(selectEntity(`${actorId}_${arrangementCode}`));

  readonly managementStatistic$ = (actorId: string) =>
    this.#managementStatistics.pipe(selectEntity(actorId));

  fetchManagementResult(actorId: string, arrangementCode: string) {
    return this.#managementService
      .fetchResultForArrangementByActor(actorId, arrangementCode)
      .pipe(
        map((managementResult: ManagementResult | Array<Result>) => {
          const normalizedResults = Array.isArray(managementResult)
            ? managementResult
            : (managementResult.results ??
              (managementResult as unknown as { data?: Array<Result> }).data ??
              []);

          return {
            ...(Array.isArray(managementResult) ? {} : managementResult),
            results: normalizedResults,
            identifier: `${actorId}_${arrangementCode}`,
          } as ManagementResult;
        }),
        tap((managementResult: ManagementResult) => {
          this.#upsertManagementResult(managementResult);
        }),
      );
  }

  #upsertManagementResult(managementResult: ManagementResult) {
    this.#managementResults.update(upsertEntities([managementResult]));
  }

  #upsertManagementStatistic(managementStatistic: ManagementStatistic) {
    this.#managementStatistics.update(upsertEntities([managementStatistic]));
  }

  fetchManagementStatistics(actorId: string) {
    return this.#managementService.fetchStatisticsByActor(actorId).pipe(
      map(
        (
          managementStatistic:
            | ManagementStatistic
            | ManagementStatistic['statistics'],
        ) => {
          const normalizedStatistics = Array.isArray(managementStatistic)
            ? managementStatistic
            : (managementStatistic.statistics ?? []);

          return {
            ...(Array.isArray(managementStatistic) ? {} : managementStatistic),
            statistics: normalizedStatistics,
            identifier: actorId,
          } as ManagementStatistic;
        },
      ),
      tap((managementStatistic: ManagementStatistic) => {
        this.#upsertManagementStatistic(managementStatistic);
      }),
    );
  }

  mergeActorResults(
    formerActorId: string,
    targetActorId: string,
    arrangementCode?: string,
  ) {
    return this.#managementService.mergeActorResults(
      formerActorId,
      targetActorId,
      arrangementCode,
    );
  }

  fetchMergeConflicts(
    formerActorId: string,
    targetActorId: string,
    arrangementCode?: string,
  ) {
    return this.#managementService
      .fetchMergeConflicts(formerActorId, targetActorId, arrangementCode)
      .pipe(
        tap((response: ManagementMergeConflictsResponse) => {
          this.#managementMergeConflictsSubject.next(response);
        }),
      );
  }
}
